import fetch from 'cross-fetch';
import merge from 'lodash/merge';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import { isFeatureActive } from '@helpers';
import { get } from '@helpers/cookie';
import { error as DDErrorLogger } from '@helpers/logger';
import { getUrlParameter } from '@helpers/urlHelper';
import md5 from '../../../externals/md5';

const formDataContentType = 'multipart/form-data';
const emptyResponseStatuses = [204, 205];
const { GET, HEAD, OPTIONS } = {
    GET: 'GET',
    HEAD: 'HEAD',
    OPTIONS: 'OPTIONS'
};

const hasToParseResponseAsJson = (response) => {
    const { headers, status } = response;
    const contentType = headers.get('Content-Type');

    return emptyResponseStatuses.indexOf(status) === -1 && contentType && contentType.indexOf('json') !== -1;
};

const applyGetRequestParameters = (requestUrl, parameters) => [
    requestUrl,
    Object.keys(parameters)
        .map(dataKey => `${dataKey}=${encodeURIComponent(parameters[dataKey])}`)
        .join('&')
].join('?');

export const getCsrfToken = () => {
    const cookieValue = get('bzt');

    if (cookieValue) {
        return md5(cookieValue);
    }

    return null;
};

const isRequestMethodSafe = requestOptions => ([
    GET,
    HEAD,
    OPTIONS
].indexOf(requestOptions.method) > -1);

const getRequestOptions = (url, options) => {
    const { request, apiVersion } = options;
    const requestOptions = merge(
        {
            credentials: 'same-origin',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json; charset=utf-8',
                'X-Requested-With': 'XMLHttpRequest',
                ...apiVersion ? {
                    'X-API-Version2': apiVersion
                } : {}
            },
            method: GET
        },
        request
    );

    if (requestOptions.headers['Content-Type'] === formDataContentType) {
        delete requestOptions.headers['Content-Type']; // needed for suitable boundary to be picked for file uploads
    }

    if (/^https?:\/\//.test(url)) {
        delete requestOptions.headers['X-Requested-With'];
    }

    if (!isRequestMethodSafe(requestOptions)) {
        requestOptions.headers['Csrf-Token'] = getCsrfToken();
    }

    return requestOptions;
};

const replaceUrlParameter = (url, parameterName, parameterValue) => url.replace(`{${parameterName}}`, parameterValue);

const formatDataForRequestBody = (data, requestOptions) => {
    if (requestOptions.headers && requestOptions.headers['Content-Type'] === formDataContentType) {
        return data;
    }

    return JSON.stringify(data);
};

const getFailedRequestResponse = (responseData, fetchResponse, hasToFormatFailedRequest) => {
    if (hasToFormatFailedRequest) {
        return {
            data: responseData,
            status: fetchResponse?.status
        };
    }

    return responseData;
};

const applyUrlParameters = (url, parameters) => {
    const { store, language } = App.Config;
    const defaultUrlParameters = { store, language };
    const parametersWithDefaults = { ...defaultUrlParameters, ...parameters };
    let newUrl = url;

    Object
        .keys(parametersWithDefaults)
        .forEach((parameter) => {
            newUrl = replaceUrlParameter(newUrl, parameter, parametersWithDefaults[parameter]);
        });

    return newUrl;
};

const isFormData = data => data instanceof FormData;

const getRequestData = (data, urlParametersList, options) => {
    let requestData = data;

    if (getUrlParameter('show_keys') === '1' && options.method === GET) {
        requestData = { ...requestData, show_keys: 1 };
    }

    if (requestData) {
        // In case of form data we don't want to look for possible parameters to omit
        if (isFormData(requestData)) {
            return requestData;
        }

        return omit(requestData, urlParametersList);
    }

    return null;
};

const getResponseData = (response, requestError) => {
    if (response) {
        if (hasToParseResponseAsJson(response)) {
            return response.json();
        }

        return response.text();
    }

    if (requestError.error_message) {
        return requestError;
    }

    return { error_message: requestError.message };
};

const requestResponseInterceptors = [];

export const registerRequestResponseInterceptor = (interceptor) => {
    requestResponseInterceptors.push(interceptor);
};

export const unregisterRequestResponseInterceptor = (interceptor) => {
    const index = requestResponseInterceptors.indexOf(interceptor);

    if (index !== -1) {
        requestResponseInterceptors.splice(index, 1);
    }
};

export const handleRequest = async (url, data, options = {}) => {
    const { onAbort, hasToFormatFailedRequest } = options;
    const fetchOptions = getRequestOptions(url, options);
    // eslint-disable-next-line compat/compat
    const abortControllerInstance = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const urlParametersList = url.match(/[^{}]+(?=})/g);
    const requestData = getRequestData(data, urlParametersList, fetchOptions);
    const urlParameters = data && !isFormData(data) ? pick(data, urlParametersList) : {};

    let requestUrl = urlParametersList
        ? applyUrlParameters(
            url,
            urlParameters
        )
        : url;

    if (abortControllerInstance) {
        fetchOptions.signal = abortControllerInstance.signal;

        if (onAbort) {
            onAbort(abortControllerInstance.abort);
        }
    }

    if (requestData) {
        if ([GET, HEAD].indexOf(fetchOptions.method) > -1) {
            requestUrl = applyGetRequestParameters(requestUrl, requestData);
        } else {
            fetchOptions.body = formatDataForRequestBody(requestData, options.request);
        }
    }

    let fetchResponse;
    let fetchError;

    try {
        const baseUrl = process.env.NODE_ENV === 'test' ? window.location.origin : '';
        fetchResponse = await fetch(`${baseUrl}${requestUrl}`, fetchOptions);
    } catch (error) {
        fetchError = error;
    }

    if (fetchResponse?.redirected && App?.Feature?.isActive('T74868_redirect_to')) {
        throw new Error(['Unexpected redirect', url, fetchResponse.url].join(' : '));
    }

    const responseData = await getResponseData(fetchResponse, fetchError);
    let promise;

    if (fetchResponse?.ok) {
        promise = new Promise((resolve) => {
            resolve(responseData);
        });
    } else {
        if (typeof responseData === 'string') {
            const message = `front_end.api_timeout_${requestUrl}`;

            DDErrorLogger(message, {
                responseData: isFeatureActive('PLT-90215-request-with-timeout-response-update') ? responseData : message,
                requestUrl
            });
        }

        promise = new Promise((resolve, reject) => {
            reject(getFailedRequestResponse(
                responseData,
                fetchResponse,
                hasToFormatFailedRequest
            ));
        });
    }

    if (requestResponseInterceptors.length > 0) {
        requestResponseInterceptors.forEach(interceptor => interceptor(fetchResponse));
    }

    return promise;
};
